import { PackageX } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  Field,
  Select,
  useToast,
} from '@/components/ui';
import {
  type NoticeStatus,
  type OutOfStockNotice,
  useMarkNoticeSeen,
  useOutOfStockNotices,
} from '@/features/out-of-stock/api';
import { useMe } from '@/features/auth/session-context';
import { isApiError } from '@/lib/api';
import { when } from './rx-request-format';
import styles from './OutOfStockTab.module.css';

/**
 * RL-1640: "Out of stock" in the doctor's inbox: the dispensary's "Notify
 * doctor" notices (RL-1639) with the medicines and the dispenser's note.
 * `Change prescription` opens the visit; `Mark seen` clears the notice (the
 * prescriber only). The manager's oversight view lists the branch's notices
 * without the actions.
 */
export function OutOfStockTab({ oversight }: { oversight: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const [status, setStatus] = useState<NoticeStatus>('open');
  const q = useOutOfStockNotices(status);
  const seen = useMarkNoticeSeen();
  const me = useMe();

  const markSeen = (n: OutOfStockNotice) =>
    void seen
      .mutateAsync(n.id)
      .then(() =>
        toast.show({ tone: 'success', title: t('outOfStock.markedSeen') }),
      )
      .catch((err: unknown) =>
        toast.show({
          tone: 'error',
          title: t('outOfStock.notDone'),
          body: isApiError(err) ? err.message : undefined,
          error: err,
        }),
      );

  return (
    <Card
      title={t('outOfStock.title')}
      flush
      actions={
        <div className={styles.filters}>
          <Field label={t('outOfStock.show')}>
            <Select
              value={status}
              options={(['open', 'seen', 'all'] as const).map((s) => ({
                value: s,
                label: t(`outOfStock.status.${s}`),
              }))}
              onChange={(event) =>
                setStatus(event.target.value as NoticeStatus)
              }
            />
          </Field>
        </div>
      }
    >
      <DataTable
        caption={t('outOfStock.title')}
        rows={q.data}
        rowKey={(n) => n.id}
        loading={q.isLoading}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty={
          <EmptyState
            icon={<PackageX size={22} />}
            title={t('outOfStock.empty')}
          />
        }
        columns={[
          {
            key: 'patient',
            header: t('outOfStock.patient'),
            render: (n) => (
              <>
                <strong>{n.patient.fullName}</strong>
                <p className="mono">{n.prescription.number ?? '-'}</p>
              </>
            ),
          },
          {
            key: 'medicines',
            header: t('outOfStock.medicines'),
            render: (n) =>
              n.medicines
                .map(
                  (m) =>
                    `${m.medicine} × ${m.quantity}${m.unit ? ` ${m.unit}` : ''}`,
                )
                .join(', '),
          },
          {
            key: 'note',
            header: t('outOfStock.note'),
            render: (n) => (
              <>
                <p>{n.note}</p>
                <p className={styles.sub}>
                  {t('outOfStock.notifiedBy', {
                    name: n.notifiedBy?.fullName ?? '-',
                    when: when(n.notifiedAt),
                  })}
                </p>
                {oversight && n.prescriber?.fullName && (
                  <p className={styles.sub}>
                    {t('outOfStock.prescriber', {
                      name: n.prescriber.fullName,
                    })}
                  </p>
                )}
              </>
            ),
          },
          {
            key: 'state',
            header: t('outOfStock.state'),
            render: (n) =>
              n.seenAt ? (
                <Badge tone="success">
                  {t('outOfStock.seenOn', { when: when(n.seenAt) })}
                </Badge>
              ) : (
                <Badge tone="warning">{t('outOfStock.status.open')}</Badge>
              ),
          },
          ...(oversight
            ? []
            : [
                {
                  key: 'actions',
                  header: (
                    <span className="visually-hidden">
                      {t('outOfStock.actions')}
                    </span>
                  ),
                  align: 'right' as const,
                  render: (n: OutOfStockNotice) => (
                    <div className={styles.filters}>
                      {/* Review decision: an open visit is changed there
                          (cancel and re-prescribe); a closed visit cannot
                          take a new prescription, so the patient's record
                          opens instead (a replacement needs a new visit). */}
                      <Button
                        size="sm"
                        title={
                          n.prescription.visitClosed
                            ? t('outOfStock.changeClosedHint')
                            : undefined
                        }
                        onClick={() =>
                          void navigate(
                            n.prescription.visitClosed
                              ? `/patients/${n.patient.id}`
                              : `/consultations/${n.prescription.encounterId}`,
                          )
                        }
                      >
                        {n.prescription.visitClosed
                          ? t('outOfStock.openRecord')
                          : t('outOfStock.change')}
                      </Button>
                      {/* Review: only the prescriber marks it seen. */}
                      {!n.seenAt && n.prescriber?.id === me.id && (
                        <Button
                          size="sm"
                          variant="primary"
                          loading={seen.isPending && seen.variables === n.id}
                          onClick={() => markSeen(n)}
                        >
                          {t('outOfStock.markSeen')}
                        </Button>
                      )}
                    </div>
                  ),
                },
              ]),
        ]}
      />
    </Card>
  );
}
